"use client";

/**
 * Naver Premium Log — wcslog.js loader + SPA route re-tracking.
 *
 * Mounts a single `<Script src="https://wcs.naver.net/wcslog.js" />`
 * in the root layout (afterInteractive strategy so initial render
 * isn't blocked). When the script resolves we run Naver's standard
 * first-visit sequence:
 *
 *   wcs_add["wa"] = "<s_xxx key>";
 *   wcs.inflow();   // referrer / campaign attribution — once per visit
 *   wcs_do();       // page-view ping for the landing URL
 *
 * For SPA navigations (App Router pushes via next/link without a
 * full page load) the initial wcslog.js execution would otherwise
 * miss every subsequent route — the script is loaded once, and
 * `wcs_do()` never fires again on its own. The usePathname effect
 * below re-calls `wcs_do()` whenever the pathname changes after the
 * script is ready, so each client-side navigation is logged the
 * same as a full reload would be.
 *
 * Per-route admin exclusion is intentionally NOT implemented — the
 * brief allows whole-site inclusion ("간단하면 제외, 복잡하면 전체
 * 포함도 허용"), and the slight admin noise is well below the noise
 * floor on real ad traffic anyway.
 */

import Script from "next/script";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

const NAVER_KEY = "s_55a78a08e61b";

export default function NaverAnalytics() {
  const pathname = usePathname();
  // `ready` flips true the moment wcslog.js finishes loading and its
  // onLoad handler has installed wcs_add. Pathname-driven re-tracks
  // are no-ops before this — without `ready` gating, we'd race the
  // script and lose the very first page view.
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!ready) return;
    if (typeof window === "undefined") return;
    try {
      window.wcs_do?.();
    } catch (e) {
      console.warn("[naver-analytics] wcs_do on route change failed:", e);
    }
  }, [pathname, ready]);

  return (
    <Script
      id="naver-wcslog"
      src="https://wcs.naver.net/wcslog.js"
      strategy="afterInteractive"
      onLoad={() => {
        if (typeof window === "undefined") return;
        if (!window.wcs_add) window.wcs_add = {};
        window.wcs_add["wa"] = NAVER_KEY;
        try {
          window.wcs?.inflow?.();
        } catch (e) {
          console.warn("[naver-analytics] inflow failed:", e);
        }
        try {
          window.wcs_do?.();
        } catch (e) {
          console.warn("[naver-analytics] initial wcs_do failed:", e);
        }
        setReady(true);
      }}
    />
  );
}
